<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { locale } from "../locale";
import { desiredMediaState } from "../states/media/state";
import { mediaActions } from "../states/media/actions";
import { saveCustomBackgroundImage } from "../states/media/customBackground";
import { BACKGROUND_PRESETS } from "../utils/backgrounds";
import { segmenterReady } from "../utils/segmenter";
import { InformationModal } from "../utils/programmingModals";
import IconBackgroundBlurLeft from "./icons/IconBackgroundBlurLeft.vue";
import IconBackgroundBlurRight from "./icons/IconBackgroundBlurRight.vue";
import type { BackgroundEffect } from "../states/media/state";

const mode = inject("colormode", ref("white"));
const fileInput = ref<HTMLInputElement | null>(null);
const processing = ref(false);

const customBackground = computed(() => desiredMediaState.customBackground);
const effectsDisabled = computed(() => !segmenterReady.value);

const BACKGROUND_IMAGE_MAX_SIZE = 10 * 1024 * 1024; // 10 МБ
const BACKGROUND_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "tif", "tiff", "webp"]);

function isAllowedBackgroundImage(file: File): boolean {
  if (file.size > BACKGROUND_IMAGE_MAX_SIZE) return false;
  const dot = file.name.lastIndexOf(".");
  if (dot === -1) return false;
  return BACKGROUND_IMAGE_EXTENSIONS.has(file.name.slice(dot + 1).toLowerCase());
}

function isActive(effect: BackgroundEffect): boolean {
  const current = desiredMediaState.backgroundEffect;
  switch (effect.kind) {
    case "none":
      return current.kind === "none";
    case "blur":
      return current.kind === "blur" && current.strength === effect.strength;
    case "preset":
      return current.kind === "preset" && current.id === effect.id;
    case "custom":
      return current.kind === "custom";
  }
}

function select(effect: BackgroundEffect) {
  if (effectsDisabled.value && effect.kind !== "none") return;
  mediaActions.setBackgroundEffect(effect);
}

function openFilePicker() {
  if (effectsDisabled.value || processing.value) return;
  fileInput.value?.click();
}

async function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  if (!file || !file.type.startsWith("image/")) return;
  if (!isAllowedBackgroundImage(file)) {
    InformationModal({
      text: locale.backgroundImageLimitModal.text,
      title: locale.backgroundImageLimitModal.title,
    });
    return;
  }
  processing.value = true;
  try {
    const url = await saveCustomBackgroundImage(file);
    mediaActions.setCustomBackgroundImage(url);
  } catch (err) {
    console.error("err save custom background", err);
  } finally {
    processing.value = false;
  }
}
</script>
<template>
  <div class="background-effects" :class="[mode]">
    <div class="background-effects-title">{{ locale.backgroundEffects }}</div>
    <div class="background-effects-grid">
      <button
        type="button"
        class="bg-tile none-tile"
        :class="{ active: isActive({ kind: 'none' }) }"
        @click="select({ kind: 'none' })"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke="currentColor"
            stroke-width="1.5"
          />
          <path
            d="M18.364 5.63623L5.63604 18.3642"
            stroke="currentColor"
            stroke-width="1.5"
          />
        </svg>
      </button>

      <button
        type="button"
        class="bg-tile blur-tile"
        :class="{ active: isActive({ kind: 'blur', strength: 'weak' }) }"
        :disabled="effectsDisabled"
        @click="select({ kind: 'blur', strength: 'weak' })"
      >
        <IconBackgroundBlurLeft class="blur-icon-left" />
        <span class="blur-label">{{ locale.backgroundEffectWeak }}</span>
        <IconBackgroundBlurRight class="blur-icon-right" />
      </button>

      <button
        type="button"
        class="bg-tile blur-tile"
        :class="{ active: isActive({ kind: 'blur', strength: 'strong' }) }"
        :disabled="effectsDisabled"
        @click="select({ kind: 'blur', strength: 'strong' })"
      >
        <IconBackgroundBlurLeft strong class="blur-icon-left" />
        <span class="blur-label">{{ locale.backgroundEffectStrong }}</span>
        <IconBackgroundBlurRight strong class="blur-icon-right" />
      </button>

      <button
        v-for="preset in BACKGROUND_PRESETS"
        :key="preset.id"
        type="button"
        class="bg-tile preset-tile"
        :class="{ active: isActive({ kind: 'preset', id: preset.id }) }"
        :disabled="effectsDisabled"
        @click="select({ kind: 'preset', id: preset.id })"
      >
        <img
          class="preset-img"
          :src="preset.src"
          alt=""
          loading="lazy"
          @error="(e) => (e.target as HTMLImageElement).style.visibility = 'hidden'"
        />
      </button>

      <button
        v-if="customBackground"
        type="button"
        class="bg-tile custom-tile"
        :class="{ active: isActive({ kind: 'custom' }) }"
        :disabled="effectsDisabled"
        @click="select({ kind: 'custom' })"
      >
        <img class="preset-img" :src="customBackground" alt="" />
      </button>

      <button
        type="button"
        class="bg-tile add-tile"
        :disabled="effectsDisabled || processing"
        @click="openFilePicker"
      >
        <svg
          v-if="processing"
          class="spinner"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22 12C22 14.2013 21.2736 16.3412 19.9335 18.0876C18.5934 19.8341 16.7145 21.0895 14.5882 21.6593C12.4619 22.229 10.2069 22.0812 8.17317 21.2388C6.13939 20.3964 4.44042 18.9064 3.33975 17C2.23908 15.0936 1.79822 12.8772 2.08555 10.6947C2.37288 8.51223 3.37235 6.48552 4.92893 4.92893C6.48552 3.37235 8.51223 2.37288 10.6947 2.08555C12.8772 1.79822 15.0936 2.23907 17 3.33975L15.9021 5.24135C14.4143 4.38236 12.6846 4.03831 10.9813 4.26255C9.27807 4.48679 7.69638 5.26679 6.48158 6.48158C5.26679 7.69638 4.48679 9.27807 4.26255 10.9813C4.03831 12.6846 4.38236 14.4143 5.24135 15.9021C6.10034 17.3899 7.42625 18.5527 9.01346 19.2102C10.6007 19.8676 12.3604 19.9829 14.0199 19.5383C15.6793 19.0937 17.1457 18.1139 18.1915 16.7509C19.2373 15.3879 19.8042 13.718 19.8042 12H22Z"
            fill="currentColor"
          />
        </svg>
        <svg
          v-else
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 5V19M5 12H19"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
      </button>

      <input
        ref="fileInput"
        class="file-input"
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.tif,.tiff,.webp,image/jpeg,image/png,image/gif,image/tiff,image/webp"
        hidden
        @change="onFileChange"
      />
    </div>
  </div>
</template>
<style scoped>
.background-effects {
  padding: 0 12px 8px;
}

.background-effects-title {
  margin-bottom: 8px;
  font-family: Open Sans;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16.34px;
  color: #747474;
}

.black .background-effects-title {
  color: #a4a4a4;
}

/* Ряд = 8 «единиц» ширины (58.5px при контейнере 552px):
   обычная плитка занимает 2 (129px), blur — 3 (~199.5px).
   Ряды: [нет фона][blur][blur] = 2+3+3, пресеты 4 шт. по 2.
   Grid сам подгоняет колонки под доступную ширину — wrap невозможен. */
.background-effects-grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 12px;
}

.bg-tile {
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column: span 2;
  height: 64px;
  padding: 0;
  border-radius: 4px;
  border: 1.5px solid #747474;
  background: transparent;
  color: #a4a4a4;
  overflow: hidden;
  cursor: pointer;
  font-family: Open Sans;
}

.bg-tile:hover {
  border-color: #a4a4a4;
  background-color: #383838;
}

.bg-tile.active,
.bg-tile.active:hover {
  border-color: #8dca35;
}

.bg-tile:disabled {
  opacity: 0.45;
  cursor: default;
}

.bg-tile:disabled:hover {
  border-color: #747474;
  background-color: transparent;
}

.white .bg-tile {
  border-color: #d8d8d8;
}

.white .bg-tile:hover {
  border-color: #a4a4a4;
  background-color: #f6f6f6;
}

.white .bg-tile.active,
.white .bg-tile.active:hover {
  border-color: #8dca35;
}

.white .bg-tile:disabled,
.white .bg-tile:disabled:hover {
  border-color: #d8d8d8;
  background-color: transparent;
}

.blur-tile {
  justify-content: space-between;
  grid-column: span 3;
  padding: 0 4px;
  color: #747474;
}

.white .blur-tile {
  color: #d8d8d8;
}

.blur-icon-left,
.blur-icon-right {
  flex-shrink: 0;
}

.blur-label {
  max-width: min-content;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  color: #ffffff;
}

.white .blur-label {
  color: #292929;
}

.preset-tile,
.custom-tile {
  padding: 0;
}

.preset-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.spinner {
  animation: bg-spin 1s linear infinite;
}

@keyframes bg-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.file-input {
  display: none;
}
</style>
